import * as React from "react";
import { useState, useEffect, useRef } from "react";
import ColumnMappingGroups, { ColumnMappingGroup, MultiSelectMapping } from "./ColumnMappingGroups";
import { getAvailableDataSources, extractTableData, validateTableData } from "../../../shared/excelDataExtractor";
import { UseSmartMatchReturn } from "../../hooks/useSmartMatch";
import SpreetailHeader from "../layout/SpreetailHeader";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  generateAutoSetup,
  AutoSetupRequest,
  TableData
} from "../../../shared/apiService";
import { POLLING } from "../../../shared/constants";

interface SelectDataSourceProps {
  onNext: () => void;
  smartMatch: UseSmartMatchReturn;
}

const SelectDataSource: React.FC<SelectDataSourceProps> = ({ onNext, smartMatch }) => {
  const [leftTable, setLeftTable] = useState<string>(smartMatch.state.leftDataSource || "");
  const [rightTable, setRightTable] = useState<string>(smartMatch.state.rightDataSource || "");
  const [leftDropdownOpen, setLeftDropdownOpen] = useState<boolean>(false);
  const [rightDropdownOpen, setRightDropdownOpen] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [tableOptions, setTableOptions] = useState<string[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(true);
  
  const [userIntent, setUserIntent] = useState<string>(smartMatch.state.matchingIntent || "");
  const [isGeneratingAutoSetup, setIsGeneratingAutoSetup] = useState<boolean>(false);
  const [externalMappingGroups, setExternalMappingGroups] = useState<ColumnMappingGroup[] | undefined>(undefined);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (smartMatch.state.error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [smartMatch.state.error]);

  useEffect(() => {
    setLeftTable(smartMatch.state.leftDataSource || "");
    setRightTable(smartMatch.state.rightDataSource || "");
    setUserIntent(smartMatch.state.matchingIntent || "");
    if (smartMatch.state.leftDataSource && smartMatch.state.rightDataSource) {
      setShowValidation(false);
    }
  }, [smartMatch.state.leftDataSource, smartMatch.state.rightDataSource, smartMatch.state.matchingIntent]);

  useEffect(() => {
    const loadDataSources = async () => {
      try {
        setIsLoadingOptions(true);
        const sources = await getAvailableDataSources();
        setTableOptions(sources);
        
        if (sources.length === 0) {
          smartMatch.setError("No data sheets found. Please create Data sheets first to proceed with Smart Match.");
        }
      } catch (error) {
        console.error("Error loading data sources:", error);
        smartMatch.setError("Failed to load data sources. Please try again.");
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadDataSources();
  }, []);

  useEffect(() => {
    const POLL_INTERVAL_MS = POLLING.SMART_MATCH_INTERVAL_MS;

    const pollForNewSheets = async () => {
      try {
        const currentSources = await getAvailableDataSources();
        
        setTableOptions(prevOptions => {
          const newSheets = currentSources.filter(sheet => !prevOptions.includes(sheet));
          
          if (newSheets.length > 0) {
            console.log(`🔄 New sheets detected: ${newSheets.join(', ')}`);
            return currentSources;
          }
          
          const removedSheets = prevOptions.filter(sheet => !currentSources.includes(sheet));
          if (removedSheets.length > 0) {
            console.log(`🔄 Sheets removed: ${removedSheets.join(', ')}`);
            return currentSources;
          }
          
          return prevOptions;
        });
      } catch (error) {
        console.warn("Error polling for new sheets:", error);
      }
    };

    const pollInterval = setInterval(pollForNewSheets, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  const handleLeftTableSelect = async (table: string) => {
    setLeftTable(table);
    setLeftDropdownOpen(false);
    setShowValidation(false);
    
    try {
      smartMatch.setError(null);
      
      const tableData = await extractTableData(table);
      const validation = validateTableData(tableData);
      
      if (!validation.valid) {
        smartMatch.setError(`Left table: ${validation.message}`);
        return;
      }

      // ✅ Clear cached formulas since table changed
      // console.log('🔄 Clearing cached formulas - left table changed');
      // smartMatch.clearCachedMatchingFormulas();
      
      smartMatch.setLeftDataSource(table, tableData);
      console.log(`✅ Left table "${table}" loaded: ${tableData.rowCount} rows × ${tableData.columnCount} columns`);
    } catch (error) {
      console.error("Error loading left table:", error);
      smartMatch.setError(`Failed to load left table: ${error.message}`);
    }
  };

  const handleRightTableSelect = async (table: string) => {
    setRightTable(table);
    setRightDropdownOpen(false);
    setShowValidation(false);
    
    try {
      smartMatch.setError(null);
      
      const tableData = await extractTableData(table);
      const validation = validateTableData(tableData);
      
      if (!validation.valid) {
        smartMatch.setError(`Right table: ${validation.message}`);
        return;
      }
      
      // ✅ Clear cached formulas since table changed
      // console.log('🔄 Clearing cached formulas - right table changed');
      // smartMatch.clearCachedMatchingFormulas();

      smartMatch.setRightDataSource(table, tableData);
      console.log(`✅ Right table "${table}" loaded: ${tableData.rowCount} rows × ${tableData.columnCount} columns`);
    } catch (error) {
      console.error("Error loading right table:", error);
      smartMatch.setError(`Failed to load right table: ${error.message}`);
    }
  };

  const handleContinue = () => {
    if (leftTable && rightTable) {
      if (smartMatch.canProceedToStep2()) {
        onNext();
      } else {
        setShowValidation(true);
        smartMatch.setError("Please ensure both tables are loaded successfully");
      }
    } else {
      setShowValidation(true);
      smartMatch.setError("Please select both left and right tables");
    }
  };

  const swapColumnMappings = () => {
    console.log('🔄 Swapping column mappings to match new table positions...');
    smartMatch.swapColumnMappingsInState();
    console.log('✅ Column mappings, mapping groups, and output columns swapped successfully');
  };

  const handleGenerateAutoSetup = async () => {
    if (!smartMatch.state.leftTableData || !smartMatch.state.rightTableData) {
      smartMatch.setError("Please select both tables first");
      return;
    }

    try {
      setIsGeneratingAutoSetup(true);
      smartMatch.setError(null);

      const leftTableData: TableData = {
        table_name: smartMatch.state.leftTableData.sheetName,
        headers: smartMatch.state.leftTableData.headers,
        rows: smartMatch.state.leftTableData.data
      };

      const rightTableData: TableData = {
        table_name: smartMatch.state.rightTableData.sheetName,
        headers: smartMatch.state.rightTableData.headers,
        rows: smartMatch.state.rightTableData.data
      };

      const request: AutoSetupRequest = {
        left_table: leftTableData,
        right_table: rightTableData,
        user_intent: userIntent.trim() || null
      };

      console.log('🤖 Calling auto-setup API...');
      console.log('   Method:', userIntent.trim() ? 'Method 2 (Intent-based)' : 'Method 1 (Auto-generate)');
      
      const response = await generateAutoSetup(request);

      console.log('✅ Auto-setup response received:', response);

      const matchingIntent = response.matching_intent || null;
      setUserIntent(matchingIntent || "");
      smartMatch.setMatchingIntent(matchingIntent);

      const mappingGroups: ColumnMappingGroup[] = response.match_groups.map((group, groupIndex) => {
        const mappings: MultiSelectMapping[] = group.map((columnPair, pairIndex) => ({
          id: `mapping_${Date.now()}_${groupIndex}_${pairIndex}`,
          leftColumns: [columnPair.left_column],
          rightColumns: [columnPair.right_column]
        }));

        const operator = response.logic_operator === 'OR' && groupIndex < response.match_groups.length - 1 
          ? 'OR' 
          : 'AND';

        return {
          id: `group_${Date.now()}_${groupIndex}`,
          mappings: mappings,
          operator: operator
        };
      });

      console.log('📊 Converted mapping groups:', mappingGroups);

      setExternalMappingGroups(mappingGroups);

      smartMatch.setGroundingInfo(response.grounded_table, response.reasoning);

      await arrangeGroundedTableToLeft(response.grounded_table);

      console.log('✅ Auto-setup complete');
    } catch (error) {
      console.error('❌ Error generating auto setup:', error);
      smartMatch.setError(`Failed to generate auto setup: ${error.message}`);
    } finally {
      setIsGeneratingAutoSetup(false);
    }
  };

  const arrangeGroundedTableToLeft = async (groundedTableName: string) => {
    if (!smartMatch.state.leftTableData || !smartMatch.state.rightTableData) {
      return;
    }

    const currentLeftTableName = smartMatch.state.leftTableData.sheetName;
    const currentRightTableName = smartMatch.state.rightTableData.sheetName;

    if (groundedTableName === currentRightTableName && groundedTableName !== currentLeftTableName) {
      console.log('🔄 Auto-arranging: Moving grounded table to left side');
      
      const tempSource = smartMatch.state.leftDataSource;
      const tempData = smartMatch.state.leftTableData;
      
      smartMatch.setLeftDataSource(smartMatch.state.rightDataSource || '', smartMatch.state.rightTableData);
      smartMatch.setRightDataSource(tempSource || '', tempData);

      setLeftTable(smartMatch.state.rightDataSource || '');
      setRightTable(tempSource || '');
      
      swapColumnMappings();
      
      // ✅ FIX: Also swap the external mapping groups to match the new table positions
      setExternalMappingGroups(prev => {
        if (!prev || prev.length === 0) return prev;
        
        console.log('🔄 Swapping external mapping groups to match new table positions');
        return prev.map(group => ({
          ...group,
          mappings: group.mappings.map(mapping => ({
            ...mapping,
            leftColumns: mapping.rightColumns,  // Swap: right columns become left
            rightColumns: mapping.leftColumns   // Swap: left columns become right
          }))
        }));
      });
      
      console.log(`✅ Grounded table "${groundedTableName}" is now on the left side`);
    } else if (groundedTableName === currentLeftTableName) {
      console.log(`✅ Grounded table "${groundedTableName}" is already on the left side`);
    } else {
      console.log(`⚠️ Warning: Grounded table "${groundedTableName}" not found in current tables`);
    }
  };

  const handleIntelligentSwap = async () => {
    const leftTableNameBefore = smartMatch.state.leftTableData?.sheetName;
    const rightTableNameBefore = smartMatch.state.rightTableData?.sheetName;
    const groundedTableBefore = smartMatch.state.groundedTable;
    
    console.log('📊 Before swap:', {
      left: leftTableNameBefore,
      right: rightTableNameBefore,
      grounded: groundedTableBefore
    });
    
    // ✅ Clear formulas BEFORE swapping
    // console.log('🔄 Clearing cached formulas due to table swap');
    // smartMatch.clearCachedMatchingFormulas();
    
    const tempSource = smartMatch.state.leftDataSource;
    const tempData = smartMatch.state.leftTableData;
    
    if (smartMatch.state.rightDataSource && smartMatch.state.rightTableData) {
      smartMatch.setLeftDataSource(smartMatch.state.rightDataSource, smartMatch.state.rightTableData);
      smartMatch.setRightDataSource(tempSource || '', tempData || smartMatch.state.rightTableData);
      
      setLeftTable(smartMatch.state.rightDataSource || '');
      setRightTable(tempSource || '');
      
      console.log('🔄 Tables swapped manually by user');
      
      swapColumnMappings();
      
      // ✅ FIX: Also swap the external mapping groups to match the new table positions
      setExternalMappingGroups(prev => {
        if (!prev || prev.length === 0) return prev;
        
        console.log('🔄 Swapping external mapping groups to match manually swapped table positions');
        return prev.map(group => ({
          ...group,
          mappings: group.mappings.map(mapping => ({
            ...mapping,
            leftColumns: mapping.rightColumns,  // Swap: right columns become left
            rightColumns: mapping.leftColumns   // Swap: left columns become right
          }))
        }));
      });
      
      if (groundedTableBefore && smartMatch.state.groundingReasoning) {
        const newGroundedTable = rightTableNameBefore;
        smartMatch.setGroundingInfo(
          newGroundedTable,
          `User manually swapped tables. Previous grounding: ${smartMatch.state.groundingReasoning}`
        );
        
        console.log('📊 After swap:', {
          left: rightTableNameBefore,
          right: leftTableNameBefore,
          grounded: newGroundedTable
        });
      }
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
          .bg-white-90 {
            background-color: rgba(255, 255, 255, 0.6);
          }
          .info-banner-bg {
            background-color: rgba(4, 97, 254, 0.1);
          }
          .dropdown-scroll {
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .dropdown-scroll::-webkit-scrollbar {
            display: none;
          }
          .textarea-scroll {
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .textarea-scroll::-webkit-scrollbar {
            display: none;
          }
          textarea::placeholder {
            color: #585858;
            opacity: 1;
          }
          textarea::-webkit-input-placeholder {
            color: #585858;
            opacity: 1;
          }
          textarea::-moz-placeholder {
            color: #585858;
            opacity: 1;
          }
          textarea:-ms-input-placeholder {
            color: #585858;
            opacity: 1;
          }
        `}
      </style>
      <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
        <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none" 
             style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
        <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none" 
             style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

        <SpreetailHeader />

        <div className="flex flex-col items-center p-5 flex-1 overflow-y-auto">
          <div className="w-full max-w-md">
            <div className="text-left mb-5">
              <h1 className="text-lg font-medium leading-none text-gray-800 m-0 mb-3">
                Smart match
              </h1>
              
              <div className="flex justify-start gap-1 mb-6">
                <div className="flex-1 h-1 bg-[#0461FE] rounded-sm"></div>
                <div className="flex-1 h-1 bg-white rounded-sm"></div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center mb-1.5">
                <div className="w-5 h-5 bg-[#068E75] rounded-full flex items-center justify-center mr-2.5">
                  <span className="text-white text-[11px] font-semibold">1</span>
                </div>
                <h2 className="text-[15px] font-semibold text-gray-800 m-0">
                  Select Data Source
                </h2>
              </div>
              
              <p className="text-[13px] text-gray-500 m-0 mb-3 ml-[30px]">
                Choose tables to compare, merge or find related records
              </p>
              
              {isLoadingOptions && (
                <div className="bg-blue-50 rounded-md p-2 px-3 mb-4 flex items-center gap-2">
                  <div className="w-[18px] h-[18px] border-2 border-blue-500 border-t-transparent rounded-full" 
                       style={{ animation: 'spin 1s linear infinite' }}></div>
                  <p className="text-xs text-blue-800 m-0">
                    Loading data sources...
                  </p>
                </div>
              )}

              <div className="flex gap-2 sm:gap-4 mb-5">
                <div className="flex-1 min-w-0">
                  <label className="text-[13px] font-semibold text-gray-800 mb-1.5 block">
                    Source A
                  </label>
                  {showValidation && !leftTable && (
                    <div className="text-[11px] text-red-600 mb-1.5">
                      Please select a Source
                    </div>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => setLeftDropdownOpen(!leftDropdownOpen)}
                      className="w-full p-2.5 px-3 border border-gray-200 rounded-md bg-white-90 text-[13px] text-left cursor-pointer flex justify-between items-center overflow-hidden"
                      style={{ color: leftTable ? '#1f2937' : '#9CA3AF' }}
                      title={leftTable || "Select table"}
                    >
                      <span className="truncate mr-2">{leftTable || "Select table"}</span>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 16 16" 
                        fill="none" 
                        className={`transition-transform duration-200 flex-shrink-0 ${leftDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                      >
                        <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    {leftDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-md z-10 mt-1 max-h-48 dropdown-scroll">
                        {tableOptions.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleLeftTableSelect(option)}
                            className="w-full p-2.5 px-3 border-none bg-transparent text-[13px] text-gray-800 text-left cursor-pointer hover:bg-gray-50 overflow-hidden"
                            style={{ 
                              borderBottom: index < tableOptions.length - 1 ? "1px solid #F3F4F6" : "none" 
                            }}
                            title={option}
                          >
                            <span className="truncate block">{option}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <label className="text-[13px] font-semibold text-gray-800 mb-1.5 block">
                    Source B 
                  </label>
                  {showValidation && !rightTable && (
                    <div className="text-[11px] text-red-600 mb-1.5">
                      Please select a Source 
                    </div>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => setRightDropdownOpen(!rightDropdownOpen)}
                      className="w-full p-2.5 px-3 border border-gray-200 rounded-md bg-white-90 text-[13px] text-left cursor-pointer flex justify-between items-center overflow-hidden"
                      style={{ color: rightTable ? '#1f2937' : '#9CA3AF' }}
                      title={rightTable || "Select table"}
                    >
                      <span className="truncate mr-2">{rightTable || "Select table"}</span>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 16 16" 
                        fill="none" 
                        className={`transition-transform duration-200 flex-shrink-0 ${rightDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                      >
                        <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    {rightDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-md z-10 mt-1 max-h-48 dropdown-scroll">
                        {tableOptions.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleRightTableSelect(option)}
                            className="w-full p-2.5 px-3 border-none bg-transparent text-[13px] text-gray-800 text-left cursor-pointer hover:bg-gray-50 overflow-hidden"
                            style={{ 
                              borderBottom: index < tableOptions.length - 1 ? "1px solid #F3F4F6" : "none" 
                            }}
                            title={option}
                          >
                            <span className="truncate block">{option}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Info Banner */}
              <div className="w-full info-banner-bg rounded-lg p-2.5 flex items-center gap-2 mb-5 shadow-sm box-border">
                <div className="w-5 h-5 bg-[#0461FE] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-semibold">!</span>
                </div>
                <span className="text-[13px] text-gray-800 font-normal">
                  If only one table is selected, Smart Lookup will perform a self-match within that table.
                </span>
              </div>
            </div>

            {smartMatch.state.leftTableData && smartMatch.state.rightTableData && (
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <h2 className="text-[15px] font-semibold text-gray-800 m-0">
                    Map Columns
                  </h2>
                </div>
                
                <p className="text-[13px] text-gray-500 m-0 mb-4">
                  Let AI suggest or set manually
                </p>

                {smartMatch.state.leftTableData && smartMatch.state.rightTableData && (
                  <div className="bg-white-90 rounded-lg p-4 shadow-sm mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <img 
                          src="assets/AI-SC.png" 
                          alt="AI" 
                          className="w-7 h-7 object-contain flex-shrink-0"
                        />
                        <div>
                          <h3 className="text-[13px] font-semibold text-gray-800 m-0">
                            Matching Intent
                          </h3>
                          <p className="text-[11px] text-gray-500 m-0">
                            Clear intent boosts results.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleGenerateAutoSetup}
                        disabled={isGeneratingAutoSetup}
                        title="Generate column mappings using AI"
                        className={`text-white border-none rounded px-2 py-1 text-[11px] font-medium transition-all duration-200 flex-shrink-0 flex items-center gap-1 ${
                          isGeneratingAutoSetup 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-[#068E75] cursor-pointer hover:bg-[#057A63]'
                        }`}
                      >
                        {isGeneratingAutoSetup ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full" 
                               style={{ animation: 'spin 1s linear infinite' }}></div>
                        ) : (
                          <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                        )}
                        {isGeneratingAutoSetup ? "..." : "Generate"}
                      </button>
                    </div>
                    
                    <textarea
                      value={userIntent}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setUserIntent(newValue);
                        smartMatch.setMatchingIntent(newValue || null);
                      }}
                      disabled={isGeneratingAutoSetup}
                      placeholder="Describe your matching Intent (optional)"
                      className={`w-full h-[80px] p-3 border border-gray-200 rounded-md bg-white text-xs resize-none outline-none box-border textarea-scroll ${
                        isGeneratingAutoSetup ? 'text-gray-500 bg-gray-50' : 'text-gray-800'
                      }`}
                    />
                  </div>
                )}

                {smartMatch.state.leftTableData && smartMatch.state.rightTableData && (
                  <div className="bg-white-90 rounded-lg p-4 shadow-sm mb-4">
                    <div className="flex items-center mb-3">
                      <h3 className="text-sm font-semibold text-gray-800 m-0">
                        Table Positioning
                      </h3>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      {/* Left Table Card */}
                      <div className="flex-1 min-w-0 rounded-lg px-3 py-2 border border-gray-100" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <div className="text-[12px] font-semibold text-gray-800 truncate" title={smartMatch.state.leftTableData.sheetName}>
                          {smartMatch.state.leftTableData.sheetName}
                        </div>
                        <div className="text-[10px] text-gray-400 whitespace-nowrap">
                          {smartMatch.state.isGroundingLoading || isGeneratingAutoSetup ? (
                            <span className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                              Analyzing...
                            </span>
                          ) : smartMatch.state.groundedTable === smartMatch.state.leftTableData.sheetName ? (
                            '(Grounded)'
                          ) : smartMatch.state.groundedTable ? (
                            '(Floating)'
                          ) : (
                            '(Grounded)'
                          )}
                        </div>
                      </div>

                      {/* Swap Button */}
                      <button
                        onClick={handleIntelligentSwap}
                        disabled={smartMatch.state.isGroundingLoading || isGeneratingAutoSetup}
                        title="Swap tables"
                        className={`border-none rounded-lg px-2 py-1.5 transition-all duration-200 flex-shrink-0 flex items-center gap-1.5 ${
                          smartMatch.state.isGroundingLoading || isGeneratingAutoSetup
                            ? 'bg-gray-200 cursor-not-allowed opacity-50' 
                            : 'bg-[#E8F4FE] cursor-pointer hover:bg-[#D6EBFD]'
                        }`}
                      >
                        <img src="assets/swap.png" alt="Swap" className="w-4 h-4" />
                        <span className="text-[11px] font-medium text-[#0461FE]">Swap</span>
                      </button>

                      {/* Right Table Card */}
                      <div className="flex-1 min-w-0 rounded-lg px-3 py-2 border border-gray-100" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <div className="text-[12px] font-semibold text-gray-800 truncate" title={smartMatch.state.rightTableData.sheetName}>
                          {smartMatch.state.rightTableData.sheetName}
                        </div>
                        <div className="text-[10px] text-gray-400 whitespace-nowrap">
                          {smartMatch.state.isGroundingLoading || isGeneratingAutoSetup ? (
                            <span className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                              Analyzing...
                            </span>
                          ) : smartMatch.state.groundedTable === smartMatch.state.rightTableData.sheetName ? (
                            '(Grounded)'
                          ) : smartMatch.state.groundedTable ? (
                            '(Floating)'
                          ) : (
                            '(Floating)'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {smartMatch.state.leftTableData && smartMatch.state.rightTableData && (
                  <div className="mt-4">
                    <ColumnMappingGroups
                      smartMatch={smartMatch}
                      leftColumns={smartMatch.state.leftTableData.headers}
                      rightColumns={smartMatch.state.rightTableData.headers}
                      externalMappingGroups={externalMappingGroups}
                    />
                  </div>
                )}
              </div>
            )}

            {smartMatch.state.error && (
              <div ref={errorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
                <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">!</span>
                </div>
                <p className="text-[13px] text-red-800 m-0 leading-snug">
                  {smartMatch.state.error}
                </p>
              </div>
            )}

            <button
              onClick={handleContinue}
              disabled={isLoadingOptions}
              className={`w-full text-white border-none rounded-md p-3 px-4 text-[13px] font-semibold transition-all duration-200 shadow-md ${
                isLoadingOptions 
                  ? 'bg-gray-400 cursor-not-allowed opacity-60' 
                  : 'bg-[#068E75] cursor-pointer hover:bg-[#057A63] hover:-translate-y-px hover:shadow-lg'
              }`}
              style={{ 
                boxShadow: isLoadingOptions ? 'none' : '0 2px 4px rgba(6, 142, 117, 0.3)' 
              }}
            >
              {isLoadingOptions ? "Loading..." : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectDataSource;